/**
 * ThemeSelector
 *
 * Body of the ThemeSelectorSheet (`sheet/themeSelector/ThemeSelectorSheet.tsx`):
 * the light / dark / system theme-mode options plus a short description.
 *
 * The "Theme Preference" heading is deliberately NOT rendered here. The sheet
 * already renders it as its own title row — `SheetService.show({ title })` with
 * `<ThemeSelectorTitle />` (`ThemeSelectorSheet.tsx:32`), which reads the
 * `themeSelector` → `title` l10n key. Adding an in-content heading back would
 * show the same copy twice; do not "restore" it.
 *
 * Web-only surface — there is no Flutter counterpart to keep parity with:
 * Flutter's `MaterialApp` sets a single fixed `theme:` with no `darkTheme` and
 * no `themeMode` (`lib/main.dart:108-124`), so the app has no theme switch to
 * port.
 *
 * Colour choices are deliberate (user review, commit ef17f61) — do not "restore"
 * the earlier palette:
 *  - the content wrapper paints NO background of its own, so the sheet/dialog shell's
 *    `theme.modalBackground` shows through, matching every other sheet/modal body;
 *  - the option cards use the toggle palette (`theme.toggleActive` / `theme.toggleInactive`,
 *    the same tokens as `controls/ToggleSwitch.tsx`) for selected / unselected instead of
 *    `theme.exclusive` / `theme.surface`, so every on/off-style control reads the same.
 */

import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { ThemeMode } from '../../theme/theme';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

export const ThemeSelector: React.FC = () => {
  const { theme, themeMode, setThemeMode } = useTheme();
  const { l10n: locale } = useLocale();
  const responsive = useResponsiveDesign();

  // TODO: @claude add a follow up task for this: migrate the light/dark/system glyphs from lucide-react to AssetIcon + /assets/vectors/ per presentation.md §Icons — web-only surface, no Flutter source; deferred by task-prompt line 1 ("Let's leave the lucide-react icons in place for now")
  const themeOptions: { mode: ThemeMode; icon: React.ReactNode; labelKey: 'light' | 'dark' | 'system' }[] = [
    { mode: 'light', icon: <Sun size={responsive.icon.medium} />, labelKey: 'light' },
    { mode: 'dark', icon: <Moon size={responsive.icon.medium} />, labelKey: 'dark' },
    { mode: 'system', icon: <Monitor size={responsive.icon.medium} />, labelKey: 'system' },
  ];

  return (
    <div
      {...qaAttr({ id: 'theme-selector-content' })}
      style={{
        borderRadius: responsive.borderRadius.base,
        paddingLeft: responsive.spacing['2xl'],
        paddingRight: responsive.spacing['2xl'],
        paddingTop: responsive.themeSelector.contentPaddingVertical,
        paddingBottom: responsive.themeSelector.contentPaddingVertical,
      }}
    >
      <div
        {...qaAttr({ id: 'theme-selector-options' })}
        className="grid grid-cols-3"
        style={{ gap: responsive.gap.base }}
      >
        {themeOptions.map(({ mode, icon, labelKey }) => (
          <button
            key={mode}
            {...qaAttr({
              id: `theme-select-${mode}`,
              value: themeMode === mode ? 'selected' : undefined,
            })}
            onClick={() => setThemeMode(mode)}
            className="flex flex-col items-center hover:opacity-80"
            style={{
              backgroundColor: themeMode === mode ? theme.toggleActive : theme.toggleInactive,
              color: themeMode === mode ? theme.background : theme.text.primary,
              border: `${responsive.border.medium}px solid ${themeMode === mode ? theme.toggleActive : theme.transparent}`,
              gap: responsive.gap.sm,
              padding: responsive.spacing.lg,
              borderRadius: responsive.borderRadius.base,
              transition: `all ${ANIMATION_CONSTANTS.duration.fast}ms ${ANIMATION_CONSTANTS.easing.easeInOut}`,
            }}
          >
            {icon}
            <span
              style={{
                fontSize: responsive.font.base,
                fontWeight: responsive.fontWeight.medium,
              }}
            >
              {locale.themeSelector[labelKey]}
            </span>
          </button>
        ))}
      </div>
      <p
        {...qaAttr({ id: 'theme-selector-description' })}
        style={{
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          marginTop: responsive.spacing.lg,
        }}
      >
        {locale.themeSelector.description}
      </p>
    </div>
  );
};
